import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { TrendingUp, ShieldCheck, FileText, Mail, CheckCircle2, X, Loader2, BookOpen, BarChart3, ScrollText } from 'lucide-react';
import { toast } from 'sonner';
import INDIAN_CITIES from '../data/indianCities';

// Lightweight inline city input — uses the same dataset as <CitySelect /> but renders within the section's custom styled form
const CitySelectInline = ({ value, onChange }) => {
  const listId = React.useId();
  return (
    <>
      <input
        type="text"
        list={listId}
        value={value || ''}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder="Start typing your city..."
        autoComplete="address-level2"
        data-testid="sr-form-city"
      />
      <datalist id={listId}>
        {INDIAN_CITIES.map((c) => <option key={c} value={c} />)}
      </datalist>
    </>
  );
};

const RAZORPAY_SCRIPT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (typeof window === 'undefined') return resolve(false);
    if (window.Razorpay) return resolve(true);
    const s = document.createElement('script');
    s.src = RAZORPAY_SCRIPT_SRC;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

const StockResearchSection = () => {
  const [plan, setPlan] = useState(null);
  const [disclaimer, setDisclaimer] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', city: '' });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    axios.get(`${API}/stock-research/plans`).then((r) => {
      const p = (r.data?.plans || [])[0];
      setPlan(p);
      setDisclaimer(r.data?.disclaimer || '');
    }).catch(() => {});
  }, []);

  const handleBuy = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    if (!/^\d{10}$/.test(form.phone.replace(/\D/g, ''))) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }
    setSubmitting(true);
    try {
      // Step 1: create stock research order
      const orderRes = await axios.post(`${API}/stock-research/orders`, {
        plan_key: plan.key,
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.replace(/\D/g, ''),
        city: form.city || undefined,
      });
      const stockOrderId = orderRes.data.order_id;

      // Step 2: create razorpay payment order
      const rzpOrderRes = await axios.post(`${API}/payments/create-order`, {
        entity_type: 'stock_research_order',
        entity_id: stockOrderId,
      });
      const { order_id, amount, currency, key_id, prefill } = rzpOrderRes.data;

      // Step 3: open Razorpay checkout
      const ok = await loadRazorpayScript();
      if (!ok || !window.Razorpay) {
        toast.error('Could not load Razorpay');
        setSubmitting(false);
        return;
      }
      const rzp = new window.Razorpay({
        key: key_id,
        amount,
        currency,
        name: 'Arthik Research Desk',
        description: 'Monthly Stock Research Newsletter (Educational)',
        order_id,
        prefill: { name: prefill.name, email: prefill.email, contact: prefill.contact },
        theme: { color: '#FF6B35' },
        modal: {
          ondismiss: () => {
            setSubmitting(false);
            toast('Payment cancelled', { description: 'You can buy again anytime.' });
          },
        },
        handler: async (resp) => {
          try {
            await axios.post(`${API}/payments/verify`, {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            setSuccess({ orderId: stockOrderId, paymentId: resp.razorpay_payment_id, email: form.email });
            toast.success('Payment successful! 🎉 Report being generated…');
          } catch (e) {
            toast.error(e.response?.data?.detail || 'Verification failed');
          } finally {
            setSubmitting(false);
          }
        },
      });
      rzp.on('payment.failed', (resp) => {
        toast.error(resp?.error?.description || 'Payment failed');
        setSubmitting(false);
      });
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start checkout');
      setSubmitting(false);
    }
  };

  if (!plan) return null;

  return (
    <section className="stock-research" data-testid="stock-research-section">
      <div className="sr-wrap">
        <div className="sr-grid">
          <div className="sr-left">
            <div className="sr-eyebrow">
              <BarChart3 size={14} /> ARTHIK RESEARCH DESK • EDUCATIONAL
            </div>
            <h2 className="sr-title">
              20 Indian stocks. <span className="sr-grad">One curated PDF.</span>
            </h2>
            <p className="sr-sub">
              Every month, our analyst desk picks <strong>20 listed companies</strong> from across NIFTY 50/100 &amp; sectoral leaders, and breaks them down for serious self-directed investors — business model, financials, sector signals, risks &amp; an investor research checklist.
            </p>

            <ul className="sr-features">
              {plan.features.map((f) => (
                <li key={f}><CheckCircle2 size={16} style={{ color: '#10B981' }} /> {f}</li>
              ))}
            </ul>

            <div className="sr-trust">
              <div className="sr-trust-item"><FileText size={16} /> PDF Report</div>
              <div className="sr-trust-item"><Mail size={16} /> Instant Email</div>
              <div className="sr-trust-item"><ShieldCheck size={16} /> Razorpay Secured</div>
            </div>
          </div>

          <div className="sr-right">
            <div className="sr-card">
              <div className="sr-card-tag">{plan.highlight}</div>
              <div className="sr-card-name">{plan.name}</div>
              <div className="sr-card-price">
                <span className="sr-cur">₹</span>
                <span className="sr-amt">{plan.price}</span>
                <span className="sr-per">/ issue</span>
              </div>
              <div className="sr-card-detail">{plan.stocks_count} stocks &nbsp;·&nbsp; PDF + email &nbsp;·&nbsp; {plan.validity_days}-day access</div>

              <button
                className="sr-buy"
                onClick={() => setOpen(true)}
                disabled={submitting}
                data-testid="stock-research-buy-btn"
              >
                <TrendingUp size={18} /> Get the report — Pay ₹{plan.price}
              </button>

              <div className="sr-pay-icons">
                <span>VISA</span><span>RuPay</span><span>UPI</span><span>Mastercard</span><span>Netbanking</span>
              </div>
              <div className="sr-disclaimer">
                <BookOpen size={11} /> {disclaimer}
              </div>
            </div>
          </div>
        </div>
      </div>

      {open && !success && (
        <div className="sr-modal" role="dialog" aria-modal="true" onClick={(e) => { if (e.target.classList.contains('sr-modal')) setOpen(false); }} data-testid="stock-research-modal">
          <div className="sr-modal-card">
            <button className="sr-close" onClick={() => setOpen(false)} aria-label="Close"><X size={18} /></button>
            <div className="sr-modal-head">
              <ScrollText size={28} style={{ color: '#FF6B35' }} />
              <h3>Get your 20-stock research issue</h3>
              <p>Pay ₹{plan.price} now and we&apos;ll email your educational report within minutes.</p>
            </div>
            <div className="sr-form">
              <label>Full Name
                <input type="text" placeholder="As per your PAN / Razorpay account" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="sr-form-name" />
              </label>
              <label>Email
                <input type="email" placeholder="report will be sent here" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="sr-form-email" />
              </label>
              <label>Mobile Number
                <input type="tel" placeholder="10-digit" maxLength={10} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, '') })} data-testid="sr-form-phone" />
              </label>
              <label>City
                <CitySelectInline value={form.city} onChange={(v) => setForm({ ...form, city: v })} />
              </label>
              <button className="sr-buy sr-buy-full" onClick={handleBuy} disabled={submitting} data-testid="sr-form-submit">
                {submitting ? (<><Loader2 size={16} className="spin" /> Processing…</>) : (<><TrendingUp size={16} /> Continue to Razorpay (₹{plan.price})</>)}
              </button>
              <p className="sr-form-disclaimer">
                By continuing you agree to our educational research framework. Markets carry risk. Consult a SEBI-registered adviser before investing.
              </p>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div className="sr-modal" role="dialog" aria-modal="true" data-testid="stock-research-success">
          <div className="sr-modal-card sr-success">
            <CheckCircle2 size={56} style={{ color: '#10B981' }} />
            <h3>Payment received 🎉</h3>
            <p>Your report is being generated and will land in <strong>{success.email}</strong> within ~5 minutes.</p>
            <p className="sr-success-meta">
              <strong>Order:</strong> <span style={{ fontFamily: 'monospace' }}>{success.orderId.slice(0, 8)}</span><br/>
              <strong>Payment:</strong> <span style={{ fontFamily: 'monospace' }}>{success.paymentId.slice(0, 16)}</span>
            </p>
            <button className="sr-buy" onClick={() => { setSuccess(null); setOpen(false); setForm({ name: '', email: '', phone: '' }); }} data-testid="sr-success-close">Done</button>
          </div>
        </div>
      )}

      <style>{`
        .stock-research { padding: 4.5rem 2rem; background: linear-gradient(135deg, #0F172A 0%, #1E293B 60%, #312E81 100%); position: relative; overflow: hidden; }
        .stock-research::before { content: ''; position: absolute; top: -150px; right: -150px; width: 400px; height: 400px; background: radial-gradient(circle, rgba(255,107,53,0.18), transparent 70%); pointer-events: none; }
        .stock-research::after { content: ''; position: absolute; bottom: -100px; left: -100px; width: 300px; height: 300px; background: radial-gradient(circle, rgba(16,185,129,0.12), transparent 70%); pointer-events: none; }
        .sr-wrap { max-width: 1300px; margin: 0 auto; position: relative; z-index: 2; }
        .sr-grid { display: grid; grid-template-columns: 1.3fr 1fr; gap: 3rem; align-items: center; }
        @media (max-width: 900px) { .sr-grid { grid-template-columns: 1fr; gap: 2rem; } }
        .sr-eyebrow { display: inline-flex; align-items: center; gap: 6px; padding: 6px 14px; background: rgba(255,107,53,0.18); color: #FDBA74; border-radius: 999px; font-size: 0.7rem; font-weight: 800; letter-spacing: 0.12em; }
        .sr-title { font-family: 'Outfit', sans-serif; color: white; font-size: clamp(1.9rem, 4vw, 3rem); font-weight: 800; line-height: 1.1; margin: 14px 0 12px; }
        .sr-grad { background: linear-gradient(135deg, #FF6B35 0%, #FDBA74 100%); -webkit-background-clip: text; background-clip: text; color: transparent; }
        .sr-sub { color: #CBD5E1; font-size: 1.05rem; line-height: 1.6; margin-bottom: 1.5rem; }
        .sr-features { list-style: none; padding: 0; margin: 0 0 1.75rem; display: grid; grid-template-columns: 1fr 1fr; gap: 10px 14px; }
        @media (max-width: 540px) { .sr-features { grid-template-columns: 1fr; } }
        .sr-features li { color: #E2E8F0; font-size: 0.92rem; display: flex; align-items: flex-start; gap: 8px; }
        .sr-trust { display: flex; flex-wrap: wrap; gap: 14px; }
        .sr-trust-item { display: inline-flex; align-items: center; gap: 6px; padding: 8px 14px; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 999px; color: #CBD5E1; font-size: 0.82rem; font-weight: 600; }
        .sr-right { display: flex; justify-content: center; }
        .sr-card { background: linear-gradient(180deg, #FFFFFF 0%, #FFF7ED 100%); border-radius: 20px; padding: 2rem 1.75rem; width: 100%; max-width: 420px; box-shadow: 0 25px 70px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,107,53,0.25); position: relative; }
        .sr-card-tag { position: absolute; top: -12px; right: 24px; background: linear-gradient(135deg, #FF6B35 0%, #F97316 100%); color: white; padding: 5px 12px; border-radius: 999px; font-size: 0.7rem; font-weight: 800; letter-spacing: 0.08em; box-shadow: 0 6px 16px rgba(255,107,53,0.5); }
        .sr-card-name { color: #0F172A; font-size: 1rem; font-weight: 700; font-family: 'Outfit', sans-serif; }
        .sr-card-price { margin: 14px 0 6px; display: flex; align-items: baseline; gap: 4px; }
        .sr-cur { font-size: 1.5rem; color: #0F172A; font-weight: 700; }
        .sr-amt { font-size: 3.4rem; color: #0F172A; font-weight: 800; font-family: 'Outfit', sans-serif; line-height: 1; }
        .sr-per { color: #64748B; font-size: 0.95rem; margin-left: 6px; }
        .sr-card-detail { color: #475569; font-size: 0.85rem; margin-bottom: 1.5rem; }
        .sr-buy { width: 100%; padding: 0.95rem; background: linear-gradient(135deg, #FF6B35 0%, #F97316 100%); color: white; font-weight: 700; font-size: 0.95rem; border: 0; border-radius: 10px; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; gap: 8px; box-shadow: 0 12px 26px rgba(255,107,53,0.35); transition: transform .12s ease, box-shadow .12s ease; }
        .sr-buy:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 18px 36px rgba(255,107,53,0.5); }
        .sr-buy:disabled { opacity: 0.7; cursor: wait; }
        .sr-buy-full { margin-top: 8px; }
        .sr-pay-icons { display: flex; flex-wrap: wrap; gap: 8px; justify-content: center; margin: 1rem 0 0.75rem; }
        .sr-pay-icons span { font-size: 0.65rem; color: #64748B; padding: 2px 6px; border: 1px solid #E2E8F0; border-radius: 4px; font-weight: 700; letter-spacing: 0.04em; }
        .sr-disclaimer { display: flex; align-items: flex-start; gap: 6px; font-size: 0.7rem; color: #92400E; background: #FFFBEB; border-left: 3px solid #F59E0B; padding: 8px 10px; border-radius: 6px; line-height: 1.4; }
        .sr-modal { position: fixed; inset: 0; background: rgba(15,23,42,0.7); backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; z-index: 9999; padding: 1.5rem; animation: sr-in .2s ease; }
        @keyframes sr-in { from { opacity: 0; } to { opacity: 1; } }
        .sr-modal-card { background: white; border-radius: 18px; padding: 2.25rem 2rem 2rem; max-width: 460px; width: 100%; position: relative; max-height: 92vh; overflow-y: auto; box-shadow: 0 30px 80px rgba(0,0,0,0.4); }
        .sr-close { position: absolute; top: 14px; right: 14px; background: #F1F5F9; border: 0; width: 32px; height: 32px; border-radius: 50%; cursor: pointer; display: flex; align-items: center; justify-content: center; color: #64748B; }
        .sr-close:hover { background: #E2E8F0; color: #0F172A; }
        .sr-modal-head { text-align: center; margin-bottom: 1.5rem; }
        .sr-modal-head h3 { font-family: 'Outfit', sans-serif; font-weight: 800; color: #0F172A; font-size: 1.35rem; margin: 12px 0 6px; }
        .sr-modal-head p { color: #475569; font-size: 0.9rem; margin: 0; }
        .sr-form { display: flex; flex-direction: column; gap: 1rem; }
        .sr-form label { display: block; font-size: 0.78rem; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: 0.04em; }
        .sr-form input { width: 100%; padding: 0.85rem 1rem; margin-top: 6px; background: #F8FAFC; border: 1px solid #E2E8F0; border-radius: 10px; font-size: 0.95rem; color: #0F172A; outline: none; transition: border-color .2s ease; }
        .sr-form input:focus { border-color: #FF6B35; background: white; box-shadow: 0 0 0 4px rgba(255,107,53,0.1); }
        .sr-form-disclaimer { font-size: 0.72rem; color: #94A3B8; text-align: center; margin: 4px 0 0; line-height: 1.4; }
        .sr-success { text-align: center; }
        .sr-success h3 { font-family: 'Outfit', sans-serif; color: #0F172A; font-size: 1.4rem; margin: 14px 0 8px; }
        .sr-success p { color: #475569; font-size: 0.92rem; line-height: 1.55; margin: 0 0 0.85rem; }
        .sr-success-meta { font-size: 0.78rem; color: #475569; background: #F8FAFC; padding: 12px; border-radius: 8px; text-align: left; }
        .spin { animation: sr-spin 1s linear infinite; }
        @keyframes sr-spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
      `}</style>
    </section>
  );
};

export default StockResearchSection;
